import { Component, ChangeDetectorRef, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import * as XLSX from 'xlsx';
import { environment } from 'src/environments/environment';

interface UploadLog {
  timestamp: Date;
  prnNo: string;
  result: boolean | null;
  message: string;
  details?: any;
  expanded?: boolean;
}

@Component({
  selector: 'app-upload-student-data',
  templateUrl: './upload-student-data.component.html',
  styleUrls: ['./upload-student-data.component.scss']
})
export class UploadStudentDataComponent implements OnInit, OnDestroy {
  @ViewChild('logsContainer', { static: false }) logsContainer: ElementRef;

  file: File | null = null;
  failedRecords: any[] = [];
  showDownloadButton = false;
  successCount = 0;
  failCount = 0;
  totalRecords = 0;

  // Progress tracking
  uploadProgress = 0;
  isUploading = false;
  currentRecord = 0;
  estimatedTimeRemaining = '';
  uploadStartTime = 0;

  // Logs panel
  uploadLogs: UploadLog[] = [];
  autoScrollLogs = true;
  logsSuccessCount = 0;
  logsFailCount = 0;

  // Wake lock
  private wakeLock: any = null;
  private wakeLockInterval: any = null;
  private preventSleepVideo: HTMLVideoElement | null = null;

  // Prevent page unload during upload
  private beforeUnloadListener: any = null;
  userPermission: any;
  utp: string;

  constructor(
    private http: HttpClient,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.userPermission = JSON.parse(sessionStorage.getItem("userPermission"));
    this.utp = sessionStorage.getItem('usertypeee');
    
    // Listen for page visibility changes to re-request wake lock
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isUploading) {
        this.requestWakeLock();
      }
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.file = file;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      let jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      jsonData = jsonData.map(row => this.cleanRow(row));
      this.validateAndUpload(jsonData);
    };

    reader.readAsArrayBuffer(file);
  }

  cleanRow(row: any) {
    const cleaned: any = {};

    for (const key in row) {
      if (row.hasOwnProperty(key)) {
        let value = String(row[key] || '');
        value = value.replace(/[\u200B-\u200D\uFEFF]/g, '');

        if (key === 'Email') {
          value = value.replace(/[^a-zA-Z0-9@._-]/g, '').trim();
        }
        else if (['FirstName', 'MiddleName', 'LastName', 'MotherName'].includes(key)) {
          value = value.replace(/[^a-zA-Z ,]/g, '').replace(/\s+/g, ' ').trim();
        }
        else {
          value = value.replace(/[^a-zA-Z0-9@._/,-]/g, '').trim();
        }
        cleaned[key.trim()] = value;
      }
    }

    return cleaned;
  }

  async validateAndUpload(records: any[]) {
    this.failedRecords = [];
    this.showDownloadButton = false;
    this.successCount = 0;
    this.failCount = 0;
    this.totalRecords = records.length;
    this.uploadProgress = 0;
    this.currentRecord = 0;
    this.uploadLogs = []; // Clear previous logs
    this.logsSuccessCount = 0;
    this.logsFailCount = 0;

    // Add initial log
    this.addLog({
      timestamp: new Date(),
      prnNo: '',
      result: null,
      message: `Starting upload of ${records.length} records...`
    });

    const validRecords = records.filter((record) => {
      const validationError = this.validateRecord(record);
      if (validationError) {
        record['Result'] = 'false';
        record['Message'] = validationError;
        this.failedRecords.push(record);

        // Add validation error log
        this.addLog({
          timestamp: new Date(),
          prnNo: record.PrnNo || 'Unknown',
          result: false,
          message: `Validation Failed: ${validationError}`,
          details: record
        });

        return false;
      }
      return true;
    });

    if (this.failedRecords.length > 0) {
      this.failCount = this.failedRecords.length;

      this.addLog({
        timestamp: new Date(),
        prnNo: '',
        result: false,
        message: `${this.failedRecords.length} records failed validation. Please fix them and reupload.`
      });

      alert('Some records are invalid. Please fix them and reupload.');
      this.showDownloadButton = true;
      return;
    }

    // Request wake lock to prevent screen sleep
    await this.requestWakeLock();

    this.isUploading = true;
    this.uploadStartTime = Date.now();

    for (let count = 0; count < validRecords.length; count++) {
      const record = validRecords[count];

      // Update progress BEFORE processing (so UI updates immediately)
      this.currentRecord = count + 1;
      this.uploadProgress = Math.round(((count + 1) / validRecords.length) * 100);

      // Update time estimate (skip first few records for better accuracy)
      if (count > 2) {
        this.updateEstimatedTime(count + 1, validRecords.length);
      } else {
        this.estimatedTimeRemaining = 'Calculating...';
      }

      try {
        const res: any = await this.callApi(record);

        if (!res || res.Result === false) {
          // FAILED
          this.failCount++;
          record['Result'] = 'false';
          record['Message'] = res?.Message || 'Backend validation failed';
          this.failedRecords.push(record);

          // Add failed log
          this.addLog({
            timestamp: new Date(),
            prnNo: res?.PrnNo || record.PrnNo || 'Unknown',
            result: false,
            message: res?.Message || 'Backend validation failed',
            details: record
          });

        } else {
          // SUCCESS
          this.successCount++;

          // Add success log
          this.addLog({
            timestamp: new Date(),
            prnNo: res.PrnNo || record.PrnNo || 'Unknown',
            result: true,
            message: res.Message || 'Record imported successfully',
            details: record
          });
        }

      } catch (error) {
        // API ERROR
        this.failCount++;
        record['Result'] = 'false';
        record['Message'] = 'API Error';
        this.failedRecords.push(record);

        // Add error log
        this.addLog({
          timestamp: new Date(),
          prnNo: record.PrnNo || 'Unknown',
          result: false,
          message: 'API Error - Server unreachable or network issue',
          details: error
        });
      }

      // Force change detection every 5 records for better performance
      if (count % 5 === 0) {
        this.cdr.detectChanges();
        this.scrollLogsToBottom();
      }

      // Small delay every 50 records to allow UI to breathe
      if (count % 50 === 0 && count > 0) {
        await this.delay(10);
      }
    }

    this.failCount = this.failedRecords.length;
    this.isUploading = false;

    // Add completion log
    this.addLog({
      timestamp: new Date(),
      prnNo: '',
      result: this.failCount === 0 ? true : null,
      message: `Upload completed! Success: ${this.successCount}, Failed: ${this.failCount}`
    });

    this.cdr.detectChanges(); // Ensure final state is rendered
    this.finalizeUpload();

    // Release wake lock
    await this.releaseWakeLock();
  }

  callApi(record: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${environment.serviceUrl}/utility/ImportStudent`, record)
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(err)
        });
    });
  }

  validateRecord(record: any): string | null {
    const requiredFields = [
      'Session', 'CoCode', 'CgCode', 'PrnNo',
      'FirstName', 'Gender', 
      // 'Dob','MobNo', 'Email', 
      'FacultyCode', 'SubReg', 'SubRegCopy'
    ];

    for (const field of requiredFields) {
      if (!record[field] || record[field].trim() === '') {
        return `${field} is required`;
      }
    }
    return null;
  }

  updateEstimatedTime(current: number, total: number) {
    if (current === 0) {
      this.estimatedTimeRemaining = 'Calculating...';
      return;
    }

    const elapsed = Date.now() - this.uploadStartTime;
    const avgTimePerRecord = elapsed / current;
    const remaining = (total - current) * avgTimePerRecord;

    if (remaining < 1000) {
      this.estimatedTimeRemaining = 'Less than 1s';
      return;
    }

    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (hours > 0) {
      this.estimatedTimeRemaining = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      this.estimatedTimeRemaining = `${minutes}m ${seconds}s`;
    } else {
      this.estimatedTimeRemaining = `${seconds}s`;
    }
  }

  // ========== LOGS PANEL METHODS ==========

  addLog(log: UploadLog) {
    this.uploadLogs.unshift(log); // Add to beginning for newest first

    // Update counts
    if (log.result === true) {
      this.logsSuccessCount++;
    } else if (log.result === false) {
      this.logsFailCount++;
    }

    // Keep only last 500 logs to prevent memory issues
    if (this.uploadLogs.length > 500) {
      this.uploadLogs = this.uploadLogs.slice(0, 500);
    }

    // Auto-scroll to bottom if enabled
    if (this.autoScrollLogs) {
      setTimeout(() => this.scrollLogsToBottom(), 50);
    }
  }

  scrollLogsToBottom() {
    if (this.logsContainer && this.autoScrollLogs) {
      try {
        const container = this.logsContainer.nativeElement;
        container.scrollTop = 0; // Scroll to top since we're adding newest first
      } catch (err) {
        // Silently fail if element not ready
      }
    }
  }

  clearLogs() {
    if (confirm('Are you sure you want to clear all logs?')) {
      this.uploadLogs = [];
      this.logsSuccessCount = 0;
      this.logsFailCount = 0;
    }
  }

  toggleLogDetails(index: number) {
    this.uploadLogs[index].expanded = !this.uploadLogs[index].expanded;
  }

  // ========== WAKE LOCK & UTILITY METHODS ==========

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async requestWakeLock() {
    try {
      // Method 1: Screen Wake Lock API (Chrome, Edge)
      if ('wakeLock' in navigator) {
        this.wakeLock = await (navigator as any).wakeLock.request('screen');
        console.log('Wake Lock activated');

        // Re-request wake lock if it's released
        this.wakeLock.addEventListener('release', () => {
          console.log('Wake Lock released, re-requesting...');
          if (this.isUploading) {
            setTimeout(() => this.requestWakeLock(), 1000);
          }
        });
      }
    } catch (err) {
      console.warn('Wake Lock API failed, using fallback methods:', err);
    }

    // Method 2: Fallback - Use invisible video (works on iOS Safari and older browsers)
    this.createPreventSleepVideo();

    // Method 3: Periodic activity simulation
    this.startWakeLockInterval();

    // Method 4: Prevent page unload/refresh
    this.preventPageUnload();
  }

  createPreventSleepVideo() {
    if (!this.preventSleepVideo) {
      // Create a tiny silent video that loops to prevent sleep
      this.preventSleepVideo = document.createElement('video');
      this.preventSleepVideo.setAttribute('muted', '');
      this.preventSleepVideo.setAttribute('playsinline', '');
      this.preventSleepVideo.setAttribute('loop', '');
      this.preventSleepVideo.style.position = 'fixed';
      this.preventSleepVideo.style.opacity = '0';
      this.preventSleepVideo.style.width = '1px';
      this.preventSleepVideo.style.height = '1px';
      this.preventSleepVideo.style.pointerEvents = 'none';

      // Tiny 1-second silent video data URL
      this.preventSleepVideo.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAs1tZGF0AAACrgYF//+q3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1NSByMjkwMSA3ZDBmZjIyIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxOCAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEwIHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAA+WIiIAAX//72rvzLK0cLlS4dWXuzUfLoSXL9iDB9aAAAAwAAAwAAJuKiZ0WFMeJsgAAALmAIWElDyDzETFWKgStAAAADAAADAAADAAADAAAHgvugkks0lyOD2SZ95BAAAADAQY2=';

      document.body.appendChild(this.preventSleepVideo);

      // Play the video
      this.preventSleepVideo.play().catch(err => {
        console.warn('Video prevent sleep failed:', err);
      });
    }
  }

  startWakeLockInterval() {
    // Simulate activity every 10 seconds by triggering a tiny scroll
    this.wakeLockInterval = setInterval(() => {
      if (this.isUploading) {
        // Tiny imperceptible scroll to simulate user activity
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);

        // Request animation frame to keep browser active
        requestAnimationFrame(() => { });
      }
    }, 10000);
  }

  preventPageUnload() {
    // Warn user if they try to close/refresh the page during upload
    this.beforeUnloadListener = (e: BeforeUnloadEvent) => {
      if (this.isUploading) {
        const message = 'Upload in progress! Are you sure you want to leave? All progress will be lost.';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  async releaseWakeLock() {
    // Release Wake Lock API
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('Wake Lock released');
      } catch (err) {
        console.error('Wake Lock release error:', err);
      }
    }

    // Stop and remove prevent sleep video
    if (this.preventSleepVideo) {
      this.preventSleepVideo.pause();
      this.preventSleepVideo.remove();
      this.preventSleepVideo = null;
    }

    // Clear wake lock interval
    if (this.wakeLockInterval) {
      clearInterval(this.wakeLockInterval);
      this.wakeLockInterval = null;
    }

    // Remove page unload listener
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }
  }

  finalizeUpload() {
    if (this.failedRecords.length > 0) {
      alert(`Upload completed with some errors.
       Success: ${this.successCount}
       Failed: ${this.failCount}`);
      this.showDownloadButton = true;
    } else {
      alert(`All ${this.successCount} records uploaded successfully!`);
      this.showDownloadButton = false;
    }
  }

  downloadFailedExcel() {
    if (this.failedRecords.length === 0) {
      alert('No failed records to download.');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(this.failedRecords);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'FailedRecords');
    XLSX.writeFile(wb, 'Failed_Records.xlsx');
  }

  ngOnDestroy() {
    // Clean up wake lock when component is destroyed
    this.releaseWakeLock();
  }
}